import { useEffect, useRef, useState, type MouseEvent } from 'react';

const STORAGE_KEY = 'syncly-discovery-chat-panel-width';
const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 360;
const MAX_WIDTH = 640;
const MIN_RESULTS_WIDTH = 720;

export function useDiscoveryPanelWidth() {
  const [width, setWidth] = useState(loadDiscoveryPanelWidth);
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(DEFAULT_WIDTH);

  useEffect(() => {
    saveDiscoveryPanelWidth(width);
  }, [width]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleWindowResize = () => {
      setWidth((previous) => clampDiscoveryPanelWidth(previous));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, []);

  const handleResizeStart = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    isResizingRef.current = true;
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = width;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
      if (!isResizingRef.current) return;

      const deltaX = moveEvent.clientX - resizeStartXRef.current;
      setWidth(clampDiscoveryPanelWidth(resizeStartWidthRef.current + deltaX));
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return {
    handleResizeStart,
    width,
  };
}

export function loadDiscoveryPanelWidth() {
  if (typeof window === 'undefined') return DEFAULT_WIDTH;

  try {
    const storedWidth = window.localStorage.getItem(STORAGE_KEY);
    if (!storedWidth) return DEFAULT_WIDTH;

    const parsedWidth = Number(storedWidth);
    if (!Number.isFinite(parsedWidth)) return DEFAULT_WIDTH;

    return clampDiscoveryPanelWidth(parsedWidth);
  } catch (error) {
    console.warn('Failed to load discovery chat panel width:', error);
    return DEFAULT_WIDTH;
  }
}

export function saveDiscoveryPanelWidth(width: number) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  } catch (error) {
    console.warn('Failed to save discovery chat panel width:', error);
  }
}

export function clampDiscoveryPanelWidth(width: number) {
  return Math.min(getMaxDiscoveryPanelWidth(), Math.max(MIN_WIDTH, width));
}

function getMaxDiscoveryPanelWidth() {
  if (typeof window === 'undefined') return MAX_WIDTH;

  const viewportLimitedMaxWidth = window.innerWidth - MIN_RESULTS_WIDTH;
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, viewportLimitedMaxWidth));
}
